import { Box, CssBaseline, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { PageTitle } from '../../../components/PageTitle';
import JstLogo from '../../../assets/pictures/JST-by-scifree-flat-teal.png';
import { useAuth } from '../../../contexts/auth/useAuth';
import { SignInCard } from './SignInCard';
import { WaveFooter } from './WaveFooter';
import {
  DoneAll,
  HandshakeOutlined,
  PaletteOutlined,
  TravelExploreOutlined,
  VerifiedUserOutlined,
} from '@mui/icons-material';

/**
 * `returnTo` must be a same-origin relative path, so the page can't be used to
 * send someone to another site after they sign in.
 */
export function safeReturnTo(value: string | null): string {
  if (
    !value ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    value === '/welcome' ||
    value.startsWith('/welcome?') ||
    value.startsWith('/welcome/')
  ) {
    return '/';
  }
  return value;
}

// One icon per point in `auth.welcome.panel.points`, in the same order.
const POINT_ICONS = [
  HandshakeOutlined,
  TravelExploreOutlined,
  PaletteOutlined,
  VerifiedUserOutlined,
];

function InfoColumn() {
  const { t } = useTranslation();
  const points = t('auth.welcome.panel.points', {
    returnObjects: true,
  }) as string[];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: { xs: 'center', md: 'flex-start' },
        gap: { xs: 16, md: 32 },
        p: 32,
        maxWidth: { md: 500 },
        color: 'var(--color-scifree-teal)',
      }}
    >
      <Typography
        variant="h1"
        sx={{
          color: 'inherit',
          textAlign: { xs: 'center', md: 'left' },
          whiteSpace: 'nowrap',
          fontSize: { xs: 40, md: 56 },
          fontWeight: 700,
        }}
      >
        {t('auth.welcome.panel.welcomeTo')}
        <Box
          component="img"
          src={JstLogo}
          alt={t('auth.welcome.panel.logoAlt') as string}
          // The bottom third of the image is "by SciFree"; drop it below the
          // baseline so "JST" lines up with the text.
          sx={{
            display: 'inline-block',
            height: { xs: 40, md: 56 },
            ml: 12,
            verticalAlign: { xs: '-13px', md: '-18px' },
          }}
        />
      </Typography>
      <Typography
        variant="h2"
        sx={{
          color: 'inherit',
          textAlign: { xs: 'center', md: 'left' },
        }}
      >
        {t('auth.welcome.panel.description')}
      </Typography>
      <Box
        component="ul"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 4, md: 24 },
          m: 0,
          p: 0,
          listStyle: 'none',
        }}
      >
        {Array.isArray(points) &&
          points.map((point, index) => {
            const Icon = POINT_ICONS[index] ?? DoneAll;
            return (
              <Typography
                key={point}
                component="li"
                variant="h4"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: 'inherit',
                }}
              >
                <Icon aria-hidden="true" sx={{ fontSize: 16, flexShrink: 0 }} />
                {point}
              </Typography>
            );
          })}
      </Box>
    </Box>
  );
}

function Welcome() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));

  // Also how a sign-in leaves this page: verifyOtp sets isAuthenticated.
  if (isAuthenticated) {
    return <Navigate to={returnTo} replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-light-greyish-blue)',
      }}
    >
      <CssBaseline />
      <PageTitle title={t('auth.welcome.pageTitle')} />
      <Box
        component="main"
        id="main-content"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 16,
          py: 32,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 32,
            width: '100%',
            maxWidth: 896,
          }}
        >
          <InfoColumn />
          <SignInCard />
        </Box>
      </Box>
      <WaveFooter />
    </Box>
  );
}

export default Welcome;
